import { useSelector } from 'store';
import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { handleErr } from 'utils/functions';
import { FieldValues, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import {
  FormControl,
  GridLegacy,
  InputAdornment,
  ToggleButton,
  Typography,
} from '@mui/material';
import {
  HookFormInput,
  HookFormToggleButton,
} from 'ui-component/HookFormComponents';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import {
  formConstants,
  formValidation,
} from 'views/purchasing/OrderPreferences/constants';
import { OrderApprovalThresholdDirection } from 'types/org';
import useSnackbar from 'hooks/useSnackbar';

const OrderPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const { activeOrgId } = useSelector((state) => state.org);

  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const defaultValues = {
    [formConstants.orderApprovalThreshold.name]: org?.orderApprovalThreshold
      ? parseFloat(org?.orderApprovalThreshold).toLocaleString('en-US', {
          style: 'decimal',
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : null,
    [formConstants.orderApprovalThresholdDirection.name]:
      org?.orderApprovalThresholdDirection ??
      OrderApprovalThresholdDirection.UP,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });

  const watchDirection = useWatch({
    control,
    name: formConstants.orderApprovalThresholdDirection.name,
  });

  useEffect(() => {
    if (org) {
      reset(defaultValues);
    }
  }, [org]);

  const onSubmit = async (data: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload: data,
        }).unwrap();
        dispatchSuccessSnackbar('Order preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={2}>
              <GridLegacy item xs={12}>
                <Typography sx={{ mt: 2, mb: 1 }}>
                  {formConstants.orderApprovalThreshold.description}
                </Typography>
                <HookFormInput
                  name={formConstants.orderApprovalThreshold.name}
                  label={formConstants.orderApprovalThreshold.label}
                  errors={errors}
                  control={control}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormToggleButton
                  control={control}
                  name={formConstants.orderApprovalThresholdDirection.name}
                  label={formConstants.orderApprovalThresholdDirection.label}
                  errors={errors}
                  disabled={!isEditing}
                  noDeselectAllowed
                >
                  <ToggleButton value={OrderApprovalThresholdDirection.UP}>
                    {
                      formConstants.orderApprovalThresholdDirection
                        .optionLabels[OrderApprovalThresholdDirection.UP]
                    }
                  </ToggleButton>
                  <ToggleButton value={OrderApprovalThresholdDirection.DOWN}>
                    {
                      formConstants.orderApprovalThresholdDirection
                        .optionLabels[OrderApprovalThresholdDirection.DOWN]
                    }
                  </ToggleButton>
                </HookFormToggleButton>
                <Typography sx={{ mb: 2 }}>
                  {
                    formConstants.orderApprovalThresholdDirection.description[
                      watchDirection as OrderApprovalThresholdDirection
                    ]
                  }
                </Typography>
              </GridLegacy>
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.ORDERS.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.ORDERS.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Order Preferences"
      />
    </form>
  );
};

export default OrderPreferences;
